import { computed, reactive, ref, Ref, UnwrapRef, watch } from '@vue/composition-api';
import { AxiosResponse } from 'axios';
import i18n, { i18n_t } from '@/locales/i18n';
import smh from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { ExtUserListItemData, ListTableData } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';
// 定义列表数据
export type TableData = {
  list: Array<ExtUserListItemData>;
  selectedRowKeys: Array<string>;
  selectedList: Array<ExtUserListItemData>;
};
type PageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};

type LoadMoreData = {
  list: Array<object>;
  keyword: string;
  page: number;
  pageSize: number;
  total: number;
};

// 定义列表数据处理器
type Handler = {
  keyword: Ref<string>;
  searchStatus: Ref<boolean>;
  columns: Array<any>;
  loading: Ref<Boolean>;
  tableData: UnwrapRef<TableData>;
  pageData: UnwrapRef<PageData>;
  sort: Sort;
  hasMore: Ref<boolean>;
  loadMoreData: LoadMoreData;
  methods: {
    formatList: (originList: any[]) => any[];
    pullUserList: () => Promise<void>;
    sortChange: (newSort: Sort) => void;
    switchSort: (columnName: string) => void;
    refresh: () => void;
    searchList: () => void;
    onPageSizeChange: (pageSize: number) => void;
    onCurrentChange: (current: number) => void;
  };
};
export default function listHandler(currentUser?: Ref<any>): Handler {
  // 搜索关键词
  const keyword: Ref<string> = ref('');
  const searchStatus: Ref<boolean> = ref(false);
  // 加载更多按钮
  const hasMore = computed<boolean>(() => loadMoreData.list.length < loadMoreData.total);
  // 记录不可用的行ID
  const disabledRowKeys = ref<Array<Number | String>>([]);
  // 列表的表头数据
  const columns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 16,
      disabled: ({ row, rowIndex }: { row: any; rowIndex: Number }) => disabledRowKeys.value.includes(rowIndex),
    },
    {
      colKey: 'nickname',
      title: 'title-nickname',
      titleDesc: i18n_t('用户'),
      width: 180,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'phoneNumber',
      title: i18n_t('手机号'),
      width: 120,
    },
    {
      colKey: 'enabled',
      title: 'title-enabled',
      titleDesc: i18n_t('状态'),
      width: 60,
      sorter: true,
    },
  ];
  // 列表的数据
  const tableData = reactive<TableData>({
    selectedRowKeys: [],
    selectedList: [],
    list: [],
  });
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
        value: 20,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 50 }),
        value: 50,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 100 }),
        value: 100,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 200 }),
        value: 200,
      },
    ],
  });
  const loading: Ref<boolean> = ref(false);
  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('externalUser', {
    sortBy: 'nickname',
    descending: true,
  });
  const loadMoreData = reactive<LoadMoreData>({
    list: [],
    keyword: '',
    page: 1,
    pageSize: 20,
    total: 0,
  });
  const methods = {
    formatList(originList: ExtUserListItemData[]): ExtUserListItemData[] {
      const list = originList.map((item: any) => {
        return {
          ...item,
          comment: item.comment ? item.comment : '',
        };
      });
      return list;
    },
    async pullUserList() {
      try {
        loading.value = true;
        const res: AxiosResponse<ListTableData<ExtUserListItemData>> = await smh.userAPI.getExtUserList({
          keyword: keyword.value,
          page: pageData.currPage,
          pageSize: pageData.pageSize,
          orderBy: innerSort.value.orderBy,
          orderByType: innerSort.value.orderByType,
          showPhoneNumber: true,
        });
        if (keyword.value && pageData.total !== 0) {
          searchStatus.value = true;
        } else {
          searchStatus.value = false;
        }
        loading.value = false;
        pageData.total = res.data.totalNum;
        tableData.list = methods.formatList(res.data.contents);
        tableData.list.forEach((item: any) => (item.name = item.nickname));
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
      } catch (e) {
        loading.value = false;
        tips.error(e as MsgError);
      }
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullUserList();
    },
    refresh() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      tableData.list = [];
      methods.pullUserList();
    },
    searchList() {
      pageData.currPage = 1;
      methods.refresh();
    },
    onPageSizeChange(pageSize: number) {
      pageData.pageSize = pageSize;
      methods.pullUserList();
    },
    onCurrentChange(current: number) {
      pageData.currPage = current;
      methods.pullUserList();
    },
  };
  watch(
    () => pageData.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );
  return {
    searchStatus,
    keyword,
    columns,
    loading,
    tableData,
    pageData,
    sort: tableSort,
    hasMore,
    loadMoreData,
    methods,
  };
}
